import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { UserCircle, Loader2, Eye, EyeOff } from 'lucide-react';
import { supabase, isDemoMode } from '@/lib/supabase';
import { useAppStore } from '@/stores/appStore';
import { toast } from 'sonner';

export default function MemberLogin() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setUser, setSession, setIsAuthenticated, setCurrentOrganization } = useAppStore();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      if (isDemoMode) {
        await new Promise((resolve) => setTimeout(resolve, 800));
        const demoId = 'demo-member-' + Date.now();
        const demoUser = {
          id: demoId,
          email: formData.email || 'member@tickally.com',
          full_name: 'Demo Member',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
        const demoOrg = {
          id: 'demo-org',
          name: 'Demo Organization',
          currency: 'USD',
          language: 'en',
          monthly_dues_amount: 50,
          join_code: 'DEMO1234',
          created_by: demoId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
        setUser(demoUser);
        setSession({
          access_token: 'demo-token',
          refresh_token: 'demo-refresh',
          expires_in: 3600,
          token_type: 'bearer',
          user: { ...demoUser, app_metadata: {}, user_metadata: {}, aud: 'authenticated' },
        });
        setCurrentOrganization(demoOrg, 'member');
        setIsAuthenticated(true);
        toast.success(t('auth.loginSuccess'));
        navigate('/my-record');
        return;
      }

      // Real login
      const { data, error: loginError } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      if (loginError) throw loginError;

      if (data.session && data.user) {
        // Fetch user profile
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('*')
          .eq('id', data.user.id)
          .single();

        if (userError) {
          console.error('Error fetching user profile:', userError);
        }

        setSession(data.session);
        setIsAuthenticated(true);
        setUser(userData || { id: data.user.id, email: data.user.email });

        toast.success(t('auth.loginSuccess'));
        navigate('/my-record');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : t('auth.loginError');
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-500/10 via-background to-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center space-x-2">
            <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
              <UserCircle className="h-6 w-6 text-white" />
            </div>
            <span className="text-2xl font-bold">{t('common.appName')}</span>
          </Link>
          <p className="mt-2 text-muted-foreground">Member Login</p>
        </div>

        <Card>
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl text-center">
              {t('auth.login')}
            </CardTitle>
            <CardDescription className="text-center">
              Sign in to view your records
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">
                  {t('auth.email')} *
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">
                  {t('auth.password')} *
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between text-sm">
                <Link
                  to="#"
                  className="text-primary hover:underline"
                  onClick={(e) => {
                    e.preventDefault();
                    toast.info('Password reset coming soon!');
                  }}
                >
                  {t('auth.forgotPassword')}
                </Link>
              </div>

              <Button
                type="submit"
                className="w-full bg-green-500 hover:bg-green-600"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('common.loading')}
                  </>
                ) : (
                  t('auth.signIn')
                )}
              </Button>
            </form>

            <div className="mt-6 text-center space-y-2">
              <p className="text-sm text-muted-foreground">
                {t('auth.dontHaveAccount')}{' '}
                <Link
                  to="/member-signup"
                  className="text-primary hover:underline font-medium"
                >
                  {t('auth.signUp')}
                </Link>
              </p>
              <p className="text-sm text-muted-foreground">
                Organization admin?{' '}
                <Link
                  to="/org-login"
                  className="text-primary hover:underline font-medium"
                >
                  Organization Login
                </Link>
              </p>
            </div>

            <div className="mt-4 text-center">
              <Link
                to="/"
                className="text-sm text-muted-foreground hover:text-primary"
              >
                ← Back to Home
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
